import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../lib/api";
import { useSeo } from "../components/site/Layout";
import { LEAD_STATUSES, fmtDateTime } from "./AdminLayout";
import { StatusBadge } from "./AdminLeads";

const Info = ({ k, v, id }) => v ? <div className="grid grid-cols-[180px,1fr] gap-4 py-2 border-b border-slate-100"><dt className="text-slate-600">{k}</dt><dd className="font-medium text-slate-900 break-words" data-testid={id}>{v}</dd></div> : null;

export default function AdminLeadDetail() {
  useSeo("Détail demande — admin");
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminApi.get(`/leads/${id}`).then((r) => { setLead(r.data); setStatus(r.data.status); setNotes(r.data.notes || ""); }); }, [id]);
  const save = async () => {
    setSaving(true);
    try { const r = await adminApi.patch(`/leads/${id}`, { status, notes }); setLead(r.data); toast.success("Demande mise à jour"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  if (!lead) return <p className="text-slate-600">Chargement…</p>;
  const sim = lead.simulation;
  return (
    <div data-testid="admin-lead-detail" className="max-w-5xl">
      <Link to="/admin/demandes" className="inline-flex items-center gap-2 text-navy font-semibold"><ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour</Link>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <h1 className="text-3xl">{lead.name}</h1><StatusBadge status={lead.status} testid="lead-detail-status-badge" />
      </div>
      <p className="text-slate-600 mt-1">Reçue le {fmtDateTime(lead.created_at)} — source <strong data-testid="lead-detail-source">{lead.source}</strong></p>
      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6">
          <h2 className="text-xl mb-3">Informations</h2>
          <dl>
            <Info k="Profil" v={lead.profile} id="lead-detail-profile" />
            <Info k="Organisation" v={lead.organisation} id="lead-detail-organisation" />
            <Info k="Type de structure" v={lead.structure_type} id="lead-detail-structure" />
            <Info k="Email" v={lead.email} id="lead-detail-email" />
            <Info k="Téléphone" v={lead.phone} id="lead-detail-phone" />
            <Info k="Code postal" v={lead.postal_code} id="lead-detail-cp" />
            <Info k="Besoin" v={lead.need} id="lead-detail-need" />
            <Info k="Message" v={lead.message} id="lead-detail-message" />
          </dl>
          {sim && (
            <div className="mt-6" data-testid="lead-detail-simulation">
              <h2 className="text-xl mb-3">Simulation</h2>
              <dl>
                {Object.entries(sim.answers).map(([k, v]) => <Info key={k} k={k} v={String(v)} id={`sim-answer-${k}`} />)}
                <Info k="Catégorie" v={sim.result.category_label} id="sim-result-category-admin" />
              </dl>
              <ul className="mt-4 space-y-2">
                {sim.result.aids.map((a) => <li key={a.key} className="rounded-xl bg-slate-50 p-3"><strong>{a.name}</strong> — {a.status} : {a.headline}{a.amount_text ? ` (${a.amount_text})` : ""}</li>)}
              </ul>
            </div>
          )}
        </section>
        <section className="space-y-6">
          <div className="rounded-2xl bg-white border border-slate-200 p-6 space-y-4">
            <h2 className="text-xl">Suivi</h2>
            <label className="block"><span className="font-semibold text-navy">Statut</span>
              <select className="field-input mt-2" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="lead-status-select">
                {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <label className="block"><span className="font-semibold text-navy">Notes internes</span>
              <textarea className="field-input mt-2" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="lead-notes-input" />
            </label>
            <button type="button" className="btn-primary w-full" onClick={save} disabled={saving} data-testid="lead-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Enregistrer
            </button>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="lead-email-log">
            <h2 className="text-xl mb-3">Journal des emails</h2>
            {!lead.emails?.length && <p className="text-slate-600 text-sm">Aucun envoi enregistré (en cours ou non tenté).</p>}
            <ul className="space-y-2 text-sm">
              {lead.emails?.map((e, i) => <li key={i} className={`rounded-lg p-2 ${e.status === "envoyé" ? "bg-green-50" : "bg-red-50"}`}><strong>{e.kind}</strong> → {e.to} : {e.status}{e.status !== "envoyé" && <div className="text-xs text-slate-600 break-words">{e.info}</div>}</li>)}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
